import type { InstalledCameraGeometry } from './pose/cameraInstall.ts';
import type { PackedLayout, PixelRect } from './media/packed.ts';

export interface WireLandmark {
  index: WireLandmarkIndex;
  x: number;
  y: number;
  z: number;
  visibility?: number;
  presence?: number;
}

export const WIRE_LANDMARK_INDICES = [11, 12, 23, 24, 27, 28] as const;
export type WireLandmarkIndex = (typeof WIRE_LANDMARK_INDICES)[number];

export interface PoseFrameMessage {
  kind: 'pose-frame';
  sequence: number;
  status: 'live' | 'paused' | 'stopped';
  reason?: string;
  capturePerformanceMs: number;
  captureEpochMs: number;
  clockOffsetMs: number;
  clockSynchronized: boolean;
  frameWidthPx: number;
  frameHeightPx: number;
  landmarks?: WireLandmark[];
}

export type PoseSource = 'data-channel' | 'laptop-rederives';

export interface PoseSourceRequest {
  kind: 'pose-source-request';
  revision: number;
  source: PoseSource;
}

export interface PoseSourceApplied {
  kind: 'pose-source-applied';
  revision: number;
  source: PoseSource;
}

export interface CaptureStatusMessage {
  kind: 'capture-status';
  sequence: number;
  status: 'live' | 'paused' | 'stopped';
  reason?: string;
}

export interface PackedLayoutMessage {
  kind: 'packed-layout';
  revision: number;
  layout: PackedLayout;
  colour: PixelRect;
  alpha: PixelRect;
}

export type RtcSignal =
  | { kind: 'rtc-description'; description: RTCSessionDescriptionInit }
  | { kind: 'rtc-candidate'; candidate: RTCIceCandidateInit };

export interface EditPaintedMessage {
  kind: 'edit-painted';
  editId: string;
  originEpochMs: number;
  paintedEpochMs: number;
  clockSynchronized: boolean;
}

export interface TvSeatStatusMessage {
  kind: 'seat-status';
  seat: 'tv';
  status: 'active' | 'paused';
}

export interface CameraCalibrationMessage {
  kind: 'camera-installation';
  tv: {
    id: string;
    panelWidthM: number;
    panelHeightM: number;
  };
  image: {
    widthPx: number;
    heightPx: number;
  };
  camera: InstalledCameraGeometry;
  installation: {
    mode: 'declared';
    label: 'declared, approximate';
    phoneProfileId: string;
    horizontalFovDeg: number;
  } | {
    mode: 'calibrated-offset';
    label: 'calibrated + declared offset';
    offsetKind: 'flip-in-place' | 'custom';
    reprojectionRmsPx: number;
    viewObliquityDeg: number;
  };
  configuredAtMs: number;
}

function isProperRotation(rotation: readonly number[] | undefined): boolean {
  if (rotation?.length !== 9 || !rotation.every(Number.isFinite)) return false;
  const row = (index: number): [number, number, number] => [rotation[index * 3]!, rotation[index * 3 + 1]!, rotation[index * 3 + 2]!];
  const dot = (a: readonly number[], b: readonly number[]): number => a[0]! * b[0]! + a[1]! * b[1]! + a[2]! * b[2]!;
  const rows = [row(0), row(1), row(2)];
  if (rows.some((candidate) => Math.abs(dot(candidate, candidate) - 1) > 1e-3)) return false;
  if (Math.abs(dot(rows[0]!, rows[1]!)) > 1e-3
    || Math.abs(dot(rows[0]!, rows[2]!)) > 1e-3
    || Math.abs(dot(rows[1]!, rows[2]!)) > 1e-3) return false;
  const [a, b, c, d, e, f, g, h, i] = rotation;
  const determinant = a! * (e! * i! - f! * h!) - b! * (d! * i! - f! * g!) + c! * (d! * h! - e! * g!);
  return Math.abs(determinant - 1) <= 1e-3;
}

export function isCameraCalibrationMessage(value: unknown): value is CameraCalibrationMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<CameraCalibrationMessage>;
  const installation = message.installation;
  const camera = message.camera;
  const validInstallation = installation?.mode === 'declared'
    ? installation.label === 'declared, approximate'
      && camera?.provenance === 'declared-approximate'
      && typeof installation.phoneProfileId === 'string'
      && Number.isFinite(installation.horizontalFovDeg)
      && installation.horizontalFovDeg >= 20
      && installation.horizontalFovDeg <= 150
    : installation?.mode === 'calibrated-offset'
      ? installation.label === 'calibrated + declared offset'
      && camera?.provenance === 'calibrated-declared-offset'
      && (installation.offsetKind === 'flip-in-place' || installation.offsetKind === 'custom')
      && Number.isFinite(installation.reprojectionRmsPx)
      && Number.isFinite(installation.viewObliquityDeg)
      : false;
  return message.kind === 'camera-installation'
    && validInstallation
    && Number.isFinite(camera?.focalLengthPx) && camera!.focalLengthPx > 0
    && Number.isFinite(camera?.principalPointPx?.x) && camera!.principalPointPx.x >= 0
    && Number.isFinite(camera?.principalPointPx?.y) && camera!.principalPointPx.y >= 0
    && isProperRotation(camera?.rotationCameraToTv)
    && Number.isFinite(camera?.cameraPositionInTv?.x)
    && Number.isFinite(camera?.cameraPositionInTv?.y)
    && Number.isFinite(camera?.cameraPositionInTv?.z)
    && Number.isFinite(message.tv?.panelWidthM) && message.tv!.panelWidthM > 0
    && Number.isFinite(message.tv?.panelHeightM) && message.tv!.panelHeightM > 0
    && Number.isFinite(message.image?.widthPx) && message.image!.widthPx > 0
    && Number.isFinite(message.image?.heightPx) && message.image!.heightPx > 0
    && camera!.principalPointPx.x <= message.image!.widthPx
    && camera!.principalPointPx.y <= message.image!.heightPx
    && Number.isFinite(message.configuredAtMs);
}

export function isPoseFrameMessage(value: unknown): value is PoseFrameMessage {
  if (!value || typeof value !== 'object') return false;
  const frame = value as Partial<PoseFrameMessage>;
  return frame.kind === 'pose-frame'
    && Number.isInteger(frame.sequence)
    && (frame.status === 'live' || frame.status === 'paused' || frame.status === 'stopped')
    && Number.isFinite(frame.capturePerformanceMs)
    && Number.isFinite(frame.captureEpochMs)
    && Number.isFinite(frame.clockOffsetMs)
    && typeof frame.clockSynchronized === 'boolean'
    && Number.isFinite(frame.frameWidthPx) && frame.frameWidthPx! > 0 && frame.frameWidthPx! <= 16_384
    && Number.isFinite(frame.frameHeightPx) && frame.frameHeightPx! > 0 && frame.frameHeightPx! <= 16_384
    && (frame.reason === undefined || (typeof frame.reason === 'string' && frame.reason.length <= 512))
    && (frame.landmarks === undefined || (
      Array.isArray(frame.landmarks)
      && frame.landmarks.length === WIRE_LANDMARK_INDICES.length
      && frame.landmarks.every((landmark, index) => landmark.index === WIRE_LANDMARK_INDICES[index]
        && Number.isFinite(landmark.x)
        && Number.isFinite(landmark.y)
        && Number.isFinite(landmark.z)
        && Math.abs(landmark.x) <= 10
        && Math.abs(landmark.y) <= 10
        && Math.abs(landmark.z) <= 10
        && (landmark.visibility === undefined || Number.isFinite(landmark.visibility))
        && (landmark.presence === undefined || Number.isFinite(landmark.presence)))
    ))
    && (frame.status !== 'live' || frame.landmarks?.length === WIRE_LANDMARK_INDICES.length);
}

function hasPoseSource(value: { source?: unknown }): value is { source: PoseSource } {
  return value.source === 'data-channel' || value.source === 'laptop-rederives';
}

export function isPoseSourceRequest(value: unknown): value is PoseSourceRequest {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<PoseSourceRequest>;
  return message.kind === 'pose-source-request'
    && Number.isSafeInteger(message.revision) && message.revision! >= 0
    && hasPoseSource(message);
}

export function isPoseSourceApplied(value: unknown): value is PoseSourceApplied {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<PoseSourceApplied>;
  return message.kind === 'pose-source-applied'
    && Number.isSafeInteger(message.revision) && message.revision! >= 0
    && hasPoseSource(message);
}

export function isCaptureStatusMessage(value: unknown): value is CaptureStatusMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<CaptureStatusMessage>;
  return message.kind === 'capture-status'
    && Number.isSafeInteger(message.sequence) && message.sequence! >= 0
    && (message.status === 'live' || message.status === 'paused' || message.status === 'stopped')
    && (message.reason === undefined || (typeof message.reason === 'string' && message.reason.length <= 512));
}

function isNormalizedRect(value: unknown): value is PixelRect {
  if (!value || typeof value !== 'object') return false;
  const rect = value as Partial<PixelRect>;
  return Number.isFinite(rect.x) && rect.x! >= 0 && rect.x! <= 1
    && Number.isFinite(rect.y) && rect.y! >= 0 && rect.y! <= 1
    && Number.isFinite(rect.width) && rect.width! > 0 && rect.width! <= 1
    && Number.isFinite(rect.height) && rect.height! > 0 && rect.height! <= 1
    && rect.x! + rect.width! <= 1.000_001
    && rect.y! + rect.height! <= 1.000_001;
}

export function isPackedLayoutMessage(value: unknown): value is PackedLayoutMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<PackedLayoutMessage>;
  return message.kind === 'packed-layout'
    && Number.isSafeInteger(message.revision) && message.revision! >= 0
    && (message.layout === 'side-by-side' || message.layout === 'top-bottom' || message.layout === 'alpha-strip-half')
    && isNormalizedRect(message.colour)
    && isNormalizedRect(message.alpha);
}

export function isRtcSignal(value: unknown): value is RtcSignal {
  if (!value || typeof value !== 'object') return false;
  const signal = value as Partial<RtcSignal>;
  return (signal.kind === 'rtc-description' && Boolean(signal.description))
    || (signal.kind === 'rtc-candidate' && Boolean(signal.candidate));
}

export function isEditPaintedMessage(value: unknown): value is EditPaintedMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<EditPaintedMessage>;
  return message.kind === 'edit-painted'
    && typeof message.editId === 'string'
    && Number.isFinite(message.originEpochMs)
    && Number.isFinite(message.paintedEpochMs)
    && typeof message.clockSynchronized === 'boolean';
}

export function isTvSeatStatusMessage(value: unknown): value is TvSeatStatusMessage {
  if (!value || typeof value !== 'object') return false;
  const message = value as Partial<TvSeatStatusMessage>;
  return message.kind === 'seat-status'
    && message.seat === 'tv'
    && (message.status === 'active' || message.status === 'paused');
}
